/**
 * PATH: packages/ui/src/components/settings/team-tab/team-tab-primitives.tsx
 * PURPOSE: Shared types, constants, and small UI primitives for TeamTab
 *
 * WHY: Extracted from TeamTab.tsx to keep the orchestrator under 300 lines.
 *      Toast, ConfirmDialog, getRoleIcon, and formatDate are self-contained
 *      UI pieces with no dependency on team state.
 *
 * FLOW:
 *   ┌────────────┐    ┌──────────────────┐    ┌──────────────┐
 *   │ Props/Data │───▶│ Render primitive  │───▶│ UI output    │
 *   └────────────┘    └──────────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - ../../lib/utils: cn() class merging
 */

import { useEffect } from 'react'
import {
  Users,
  Shield,
  ShieldCheck,
  X,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react'
import { cn } from '../../../lib/utils'

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

export interface TeamMember {
  id: number
  uid: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: string
  rbac_role_code: string | null
  rbac_role_name: string | null
  status: 'active' | 'inactive'
  is_temp_password: boolean
  date_joined: string | null
  last_login: string | null
  profile_image: string | null
  /**
   * RBAC V2 (MR 11 / MR 12) — capability toggles. Optional on the
   * type so older API responses (pre-MR-12 BE) don't fail strict
   * destructuring. The owner force-True rule applies server-side
   * so `true` here means "this member can use this capability"
   * regardless of whether they got it via the tier (owner) or an
   * explicit toggle.
   */
  has_finance_org_wide?: boolean
  is_org_admin_user?: boolean
  is_billing_admin?: boolean
}

export interface RoleOption {
  value: string
  label: string
  hierarchy_level?: number
}

/** Fallback RBAC role options when the /rbac/org/{id}/roles/ endpoint is unreachable */
export const FALLBACK_ROLE_OPTIONS: RoleOption[] = [
  { value: 'org_owner', label: 'Organisation Owner', hierarchy_level: 10 },
  { value: 'org_admin', label: 'Organisation Administrator', hierarchy_level: 20 },
  { value: 'project_manager', label: 'Project Manager', hierarchy_level: 30 },
  { value: 'site_supervisor', label: 'Site Supervisor / Site Manager', hierarchy_level: 50 },
  { value: 'tradesperson', label: 'Tradesperson / Skilled Worker', hierarchy_level: 80 },
  { value: 'site_worker', label: 'Site Worker / Labourer', hierarchy_level: 90 },
  { value: 'accounts_officer', label: 'Accounts Officer / Bookkeeper', hierarchy_level: 60 },
  { value: 'admin_assistant', label: 'Administrative Assistant', hierarchy_level: 65 },
  { value: 'apprentice', label: 'Apprentice / Trainee', hierarchy_level: 95 },
]

interface RoleCategoryDef {
  label: string
  minLevel: number
  maxLevel: number
}

const ROLE_CATEGORIES: RoleCategoryDef[] = [
  { label: 'Leadership',         minLevel: 0,   maxLevel: 15 },
  { label: 'Administration',     minLevel: 16,  maxLevel: 25 },
  { label: 'Management',         minLevel: 26,  maxLevel: 35 },
  { label: 'Specialist',         minLevel: 36,  maxLevel: 49 },
  { label: 'Supervisor',         minLevel: 50,  maxLevel: 59 },
  { label: 'Office & Support',   minLevel: 60,  maxLevel: 75 },
  { label: 'Field & Operations', minLevel: 76,  maxLevel: 99 },
  { label: 'General & External', minLevel: 100, maxLevel: 1000 },
]

export function groupRoleOptionsByCategory(options: RoleOption[]): { label: string; options: RoleOption[] }[] {
  const sorted = [...options].sort((a, b) => (a.hierarchy_level ?? 100) - (b.hierarchy_level ?? 100))
  const groups: { label: string; options: RoleOption[] }[] = []

  for (const cat of ROLE_CATEGORIES) {
    const matching = sorted.filter(
      r => (r.hierarchy_level ?? 100) >= cat.minLevel && (r.hierarchy_level ?? 100) <= cat.maxLevel
    )
    if (matching.length > 0) {
      groups.push({ label: cat.label, options: matching })
    }
  }

  return groups
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

const EXECUTIVE_ROLES = new Set(['org_owner', 'director', 'general_manager', 'owner'])
const ADMIN_ROLES = new Set(['org_admin', 'operations_manager', 'workshop_manager', 'admin'])

export function getRoleIcon(role: string) {
  const code = role?.toLowerCase()
  if (EXECUTIVE_ROLES.has(code)) return <ShieldCheck className="w-4 h-4 text-amber-500" />
  if (ADMIN_ROLES.has(code)) return <Shield className="w-4 h-4 text-blue-500" />
  return <Users className="w-4 h-4 text-slate-400" />
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return 'Invalid date'
  }
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

export interface ToastProps {
  message: string
  type: 'success' | 'error'
  onDismiss: () => void
}

/** Inline toast that auto-dismisses after 4 s */
export function Toast({ message, type, onDismiss }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000)
    return () => clearTimeout(timer)
  }, [onDismiss])

  return (
    <div className={cn(
      "flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium animate-in slide-in-from-top-2",
      type === 'success'
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
        : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800'
    )}>
      {type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onDismiss} className="ml-auto p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ConfirmDialog
// ---------------------------------------------------------------------------

export interface ConfirmDialogProps {
  title: string
  message: string
  confirmLabel: string
  confirmVariant?: 'danger' | 'primary'
  onConfirm: () => void
  onCancel: () => void
}

/** Confirmation dialog for destructive/important actions */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  confirmVariant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div
        className={cn(
          "w-full max-w-sm mx-4 p-5 rounded-xl shadow-xl",
          "bg-white dark:bg-slate-900",
          "border border-slate-200 dark:border-slate-700"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className={cn(
            "p-2 rounded-lg",
            confirmVariant === 'danger'
              ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
              : "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400"
          )}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white">{title}</h4>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={cn(
              "px-3 py-1.5 rounded-lg text-sm font-medium text-white",
              confirmVariant === 'danger'
                ? "bg-red-600 hover:bg-red-700"
                : "bg-teal-600 hover:bg-teal-700"
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
